/**
 * Desktop show/hide toggle for the collection filter sidebar.
 *
 * Horizon's vertical facets render as an always-visible column; its only
 * toggle opens the mobile drawer. This injects a Filter button at the left of
 * the toolbar row that swaps `fw-filters-open` on the collection wrapper —
 * the companion CSS hides the sidebar and restores the grid's full span
 * whenever the class is absent, so closed is the default.
 *
 * Filtering and sorting re-render the section, which can replace the toolbar
 * and drop the button; a MutationObserver re-injects it. The open/closed
 * class lives on the collection wrapper, which survives those re-renders, so
 * the state carries across them.
 */
(() => {
  const ICON =
    '<svg viewBox="0 0 21 20" fill="none" aria-hidden="true">' +
    '<path d="M3 5h15M6 10h9M8.5 15h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
    '</svg>';

  const setOpen = (wrapper, open) => {
    wrapper.classList.toggle('fw-filters-open', open);
    wrapper
      .querySelectorAll('.fw-filter-toggle')
      .forEach((toggle) => toggle.setAttribute('aria-expanded', String(open)));
  };

  /** "2 items" from Horizon's (hidden) products count, as a number. */
  const productCount = (wrapper) => {
    const match = (wrapper.querySelector('.products-count-wrapper')?.textContent || '').match(/\d+/);
    return match ? match[0] : null;
  };

  const inject = () => {
    document.querySelectorAll('.collection-wrapper').forEach((wrapper) => {
      const bar = wrapper.querySelector('.facets-controls-wrapper');
      const sidebar = wrapper.querySelector('.facets-block-wrapper--vertical:not(#filters-drawer)');
      if (!bar || !sidebar) return;

      if (!bar.querySelector('.fw-filter-toggle')) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fw-filter-toggle';
        button.setAttribute('aria-expanded', String(wrapper.classList.contains('fw-filters-open')));
        button.innerHTML = `${ICON}<span>Filter</span>`;
        button.addEventListener('click', () => {
          // Below the desktop breakpoint the drawer element handles filters;
          // the overlay panel is desktop-only CSS.
          if (window.innerWidth < 750) {
            document.getElementById('filters-drawer')?.open();
            return;
          }
          setOpen(wrapper, !wrapper.classList.contains('fw-filters-open'));
        });
        bar.prepend(button);
      }

      // Bottom actions row. Filters apply live, so VIEW N PRODUCTS just
      // closes the drawer onto the already-filtered grid.
      let actions = sidebar.querySelector('.fw-filter-actions');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'fw-filter-actions';

        const clear = document.createElement('button');
        clear.type = 'button';
        clear.className = 'fw-filter-actions__clear';
        clear.textContent = 'Clear all';
        clear.addEventListener('click', () => {
          const themeClear = sidebar.querySelector('.facets__clear-all-link, .facets__clear-all');
          if (themeClear) themeClear.click();
          else window.location.href = window.location.pathname;
        });

        const view = document.createElement('button');
        view.type = 'button';
        view.className = 'fw-filter-actions__view';
        view.addEventListener('click', () => setOpen(wrapper, false));

        actions.append(clear, view);
        sidebar.appendChild(actions);
      }

      const view = actions.querySelector('.fw-filter-actions__view');
      const count = productCount(wrapper);
      const label = count === '1' ? 'View 1 product' : count ? `View ${count} products` : 'View products';
      if (view.textContent !== label) view.textContent = label;

      // Mobile drawer's apply button, matched to the desktop wording.
      const seeResults = document.querySelector('#filters-drawer .facets__see-results');
      if (seeResults) {
        const n = (seeResults.textContent.match(/\d+/) || [null])[0];
        const wanted = n === '1' ? 'View 1 product' : n ? `View ${n} products` : 'View products';
        if (seeResults.textContent.trim() !== wanted) seeResults.textContent = wanted;
      }

      // Scrim clicks land on the wrapper itself — its ::after is the scrim.
      if (!wrapper.dataset.fwScrimWired) {
        wrapper.dataset.fwScrimWired = 'true';
        wrapper.addEventListener('click', (event) => {
          if (event.target === wrapper) setOpen(wrapper, false);
        });
      }
    });
  };

  /**
   * One filter section open at a time, in both the desktop panel and the
   * mobile drawer. `toggle` does not bubble, so it is caught in the capture
   * phase; closing siblings re-fires toggle for them, which the `open` guard
   * ignores.
   */
  const exclusiveAccordions = (event) => {
    const panel = event.target;
    if (!(panel instanceof HTMLDetailsElement)) return;
    if (!panel.classList.contains('facets__panel')) return;
    if (!panel.open) return;
    if (!panel.closest('.facets--vertical, .facets--drawer')) return;

    const scope = panel.closest('.facets__filters-wrapper');
    if (!scope) return;

    scope.querySelectorAll('details.facets__panel[open]').forEach((other) => {
      if (other !== panel) other.removeAttribute('open');
    });
  };

  const start = () => {
    inject();
    document.addEventListener('toggle', exclusiveAccordions, true);
    new MutationObserver(inject).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', inject);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document
      .querySelectorAll('.collection-wrapper.fw-filters-open')
      .forEach((wrapper) => setOpen(wrapper, false));
  });
})();
